(function () {
  const SLIDE_FADE_MS = 700;
  const HTML_VIDEO_SELECTOR = "video.media-content-slider__feature-media-video";
  const IFRAME_VIDEO_SELECTOR =
    "iframe.media-content-slider__feature-media-video--iframe";

  class MediaContentSlider {
    constructor(el) {
      this.el = el;
      this.navItems = Array.from(
        el.querySelectorAll(".media-content-slider__nav-item")
      );
      this.bullets = Array.from(
        el.querySelectorAll(".media-content-slider__bullet")
      );
      this.visuals = Array.from(
        el.querySelectorAll(".media-content-slider__visual")
      );
      this.total = this.visuals.length;

      if (this.total <= 1) return;

      this.current = 0;
      this.autoplay = el.dataset.autoplay === "true";
      this.autoplayDelay = parseInt(el.dataset.autoplayDelay, 10) || 6000;
      this.matchVideoDuration = el.dataset.matchVideoDuration === "true";
      this.advanceTimer = null;
      this.progressTotal = null;
      this.progressStartTs = null;
      this.progressRemaining = null;
      this.isInViewport = false;
      this.intersectionObserver = null;
      this._videoProgressCleanup = null;
      this._leaveTimer = null;
      this.eventController = new AbortController();

      this.navItems.forEach((item, i) => {
        item.addEventListener("click", () => this._goTo(i), {
          signal: this.eventController.signal,
        });
      });

      this.bullets.forEach((btn, i) => {
        btn.addEventListener(
          "click",
          (e) => {
            e.stopPropagation();
            this._goTo(i);
          },
          {
            signal: this.eventController.signal,
          }
        );

        btn.addEventListener(
          "keydown",
          (event) => {
            this._handleTabKeydown(event, i);
          },
          {
            signal: this.eventController.signal,
          }
        );
      });

      this._updateBulletStates(this.current);
      this._scrollNavItemIntoView(this.current, { instant: true });
      this._setupIntersectionObserver();

      this.visuals.forEach((visual, i) => {
        if (i !== this.current) this._pauseSlideMedia(visual);
      });
    }

    destroy() {
      this._stopProgress();
      this.eventController.abort();
      this._clearLeaveTimer();
      if (this.intersectionObserver) this.intersectionObserver.disconnect();
    }

    _clearLeaveTimer() {
      if (this._leaveTimer) {
        clearTimeout(this._leaveTimer);
        this._leaveTimer = null;
      }
    }

    _handleTabKeydown(event, index) {
      let nextIndex = null;

      if (event.key === "ArrowDown" || event.key === "ArrowRight") {
        nextIndex = (index + 1) % this.total;
      }

      if (event.key === "ArrowUp" || event.key === "ArrowLeft") {
        nextIndex = (index - 1 + this.total) % this.total;
      }

      if (event.key === "Home") {
        nextIndex = 0;
      }

      if (event.key === "End") {
        nextIndex = this.total - 1;
      }

      if (nextIndex === null) return;

      event.preventDefault();
      this._goTo(nextIndex);
      this.bullets[nextIndex]?.focus();
    }

    _getActiveHtmlVideo() {
      const visual = this.visuals[this.current];
      if (!visual) return null;
      return visual.querySelector(HTML_VIDEO_SELECTOR);
    }

    _pauseSlideMedia(visual, { reset = true } = {}) {
      if (!visual) return;

      visual.querySelectorAll(HTML_VIDEO_SELECTOR).forEach((video) => {
        video.pause();
        if (reset) {
          video.currentTime = 0;
        }
      });

      visual.querySelectorAll(IFRAME_VIDEO_SELECTOR).forEach((iframe) => {
        this._pauseIframe(iframe);
      });
    }

    _resumeSlideMedia(visual) {
      if (!visual) return;

      visual.querySelectorAll(HTML_VIDEO_SELECTOR).forEach((video) => {
        video.play().catch(() => {});
      });

      visual.querySelectorAll(IFRAME_VIDEO_SELECTOR).forEach((iframe) => {
        this._playIframe(iframe);
      });
    }

    _restartSlideMedia(visual) {
      if (!visual) return;

      visual.querySelectorAll(HTML_VIDEO_SELECTOR).forEach((video) => {
        video.loop = !this.matchVideoDuration;
        video.currentTime = 0;
        video.play().catch(() => {});
      });

      visual.querySelectorAll(IFRAME_VIDEO_SELECTOR).forEach((iframe) => {
        this._restartIframe(iframe);
      });
    }

    _pauseIframe(iframe) {
      const src = iframe.src;

      if (src.includes("youtube.com")) {
        iframe.contentWindow?.postMessage(
          '{"event":"command","func":"pauseVideo","args":""}',
          "*"
        );
        return;
      }

      if (src.includes("player.vimeo.com")) {
        iframe.contentWindow?.postMessage('{"method":"pause"}', "*");
      }
    }

    _restartIframe(iframe) {
      const src = iframe.getAttribute("src");
      if (!src) return;
      iframe.setAttribute("src", src);
    }

    _playIframe(iframe) {
      const src = iframe.src;

      if (src.includes("youtube.com")) {
        iframe.contentWindow?.postMessage(
          '{"event":"command","func":"playVideo","args":""}',
          "*"
        );
        return;
      }

      if (src.includes("player.vimeo.com")) {
        iframe.contentWindow?.postMessage('{"method":"play"}', "*");
      }
    }

    /* ── Navigation ── */

    _goTo(index) {
      if (index === this.current) return;
      this._stopProgress();
      this._clearLeaveTimer();

      const prev = this.current;
      this.current = index;
      const prevVisual = this.visuals[prev];
      const nextVisual = this.visuals[index];

      this.visuals.forEach((visual) => {
        visual.classList.remove("is-leaving");
      });

      /* Only the media panel transitions */
      prevVisual.classList.remove("is-active");
      prevVisual.classList.add("is-leaving");
      nextVisual.classList.remove("is-leaving");
      nextVisual.classList.add("is-active");
      prevVisual.setAttribute("aria-hidden", "true");
      nextVisual.setAttribute("aria-hidden", "false");

      this._leaveTimer = setTimeout(() => {
        prevVisual.classList.remove("is-leaving");
        this._leaveTimer = null;
      }, SLIDE_FADE_MS);

      this._pauseSlideMedia(prevVisual, { reset: true });
      this._restartSlideMedia(nextVisual);

      /* Update nav items (opacity) */
      this.navItems[prev].classList.remove("is-active");
      this.navItems[index].classList.add("is-active");

      this._updateBulletStates(index);
      this._scrollNavItemIntoView(index);

      if (this.autoplay && this.isInViewport) this._startProgress();
    }

    _updateBulletStates(activeIndex) {
      this.bullets.forEach((btn, i) => {
        const isActive = i === activeIndex;
        const isDone = i < activeIndex;

        btn.classList.toggle("is-active", isActive);
        btn.classList.toggle("is-done", isDone);
        btn.setAttribute("aria-current", isActive ? "true" : "false");
        btn.setAttribute("aria-selected", isActive ? "true" : "false");
        btn.setAttribute("tabindex", isActive ? "0" : "-1");

        // Explicitly reset every bullet's fill to empty. Only the active
        // bullet is animated afterwards (by the progress timer/video sync),
        // so the previously active indicator is always cleared and can never
        // leave a visible trace or show a second, duplicated fill.
        const fill = btn.querySelector(".media-content-slider__bullet-fill");
        if (fill) {
          fill.style.transition = "none";
          fill.style.transform = "scaleY(0)";
        }
      });
    }

    _scrollNavItemIntoView(index, { instant = false } = {}) {
      const nav = this.el.querySelector(".media-content-slider__nav");
      const target = this.navItems[index];
      if (!nav || !target) return;
      if (nav.scrollWidth <= nav.clientWidth + 1) return;

      target.scrollIntoView({
        behavior: instant ? "auto" : "smooth",
        block: "nearest",
        inline: "nearest",
      });
    }

    _next() {
      this._goTo((this.current + 1) % this.total);
    }

    _advanceFromVideoEnd() {
      if (!this.matchVideoDuration) return;

      // Clearing the timers also removes the video listeners, so a late
      // "ended" event and the safety timer can never advance twice.
      this._clearProgressTimers();

      const video = this._getActiveHtmlVideo();
      if (video) {
        video.loop = false;
      }

      this._next();
    }

    /* ── Progress animation ── */

    _startProgress({ resume = false } = {}) {
      if (!this.autoplay || !this.isInViewport) return;

      this._clearProgressTimers();
      if (!resume) {
        this._updateBulletStates(this.current);
        this.progressRemaining = null;
      }

      const fill = this._activeFill();
      if (!fill) return;

      const video = this._getActiveHtmlVideo();
      if (this.matchVideoDuration && video) {
        this._startVideoProgress(video, fill);
        return;
      }

      this._startTimerProgress(fill, resume);
    }

    // Timer mode: run one continuous, compositor-driven transition from the
    // current fill position to full over the (remaining) slide duration.
    _startTimerProgress(fill, resume = false) {
      const total = this.autoplayDelay;

      let fromScale = 0;
      let duration = total;

      if (resume && this.progressRemaining != null) {
        fromScale = this._currentFillScale(fill);
        duration = this.progressRemaining;
      }

      // Anchor a virtual start time so a later freeze can compute the exact
      // remaining duration, even after resuming.
      this.progressTotal = total;
      this.progressStartTs = performance.now() - (total - duration);
      this.progressRemaining = null;

      this._animateFill(fill, fromScale, 1, duration);
      this.advanceTimer = setTimeout(() => this._next(), duration);
    }

    // Video mode: transition the fill over the video's remaining playback time
    // and advance when the video ends, so the bar stays in step with the clip.
    _startVideoProgress(video, fill) {
      const run = () => {
        const durationSec = video.duration;
        if (!Number.isFinite(durationSec) || durationSec <= 0) {
          this._startTimerProgress(fill, false);
          return;
        }

        video.loop = false;

        const fromScale = Math.min(
          Math.max(video.currentTime / durationSec, 0),
          1
        );
        const remainingMs = Math.max(
          (durationSec - video.currentTime) * 1000,
          0
        );

        // Video mode advances on the media itself, so timer-based freeze state
        // is not used here.
        this.progressTotal = null;
        this.progressStartTs = null;

        this._animateFill(fill, fromScale, 1, remainingMs);

        if (video.paused) {
          video.play().catch(() => {});
        }

        const onEnded = () => this._advanceFromVideoEnd();
        video.addEventListener("ended", onEnded);
        // Safety net in case "ended" does not fire on a clipped end.
        this.advanceTimer = setTimeout(
          () => this._advanceFromVideoEnd(),
          remainingMs + 250
        );

        this._videoProgressCleanup = () => {
          video.removeEventListener("ended", onEnded);
        };
      };

      if (
        video.readyState >= 1 &&
        Number.isFinite(video.duration) &&
        video.duration > 0
      ) {
        run();
        return;
      }

      const onReady = () => run();
      video.addEventListener("loadedmetadata", onReady, { once: true });
      this._videoProgressCleanup = () => {
        video.removeEventListener("loadedmetadata", onReady);
      };
    }

    _clearProgressTimers() {
      if (this.advanceTimer) {
        clearTimeout(this.advanceTimer);
        this.advanceTimer = null;
      }
      if (this._videoProgressCleanup) {
        this._videoProgressCleanup();
        this._videoProgressCleanup = null;
      }
    }

    _stopProgress({ freeze = false } = {}) {
      this._clearProgressTimers();

      const fill = this._activeFill();

      if (freeze && fill) {
        // Freeze the fill at its current position (used when the section
        // scrolls out of view) so it can resume seamlessly.
        const scale = this._currentFillScale(fill);
        fill.style.transition = "none";
        fill.style.transform = `scaleY(${scale})`;

        if (this.progressStartTs != null && this.progressTotal != null) {
          const elapsed = performance.now() - this.progressStartTs;
          this.progressRemaining = Math.max(this.progressTotal - elapsed, 0);
        }
        return;
      }

      this.progressRemaining = null;
      this.progressStartTs = null;
      this.progressTotal = null;
    }

    _activeFill() {
      const bullet = this.bullets[this.current];
      if (!bullet) return null;
      return bullet.querySelector(".media-content-slider__bullet-fill");
    }

    _currentFillScale(fill) {
      const value = getComputedStyle(fill).transform;
      if (!value || value === "none") return 0;
      try {
        const matrix = new DOMMatrixReadOnly(value);
        return Math.min(Math.max(matrix.d, 0), 1);
      } catch (error) {
        return 0;
      }
    }

    _animateFill(fill, fromScale, toScale, durationMs) {
      // Set the start position with no transition, force the browser to commit
      // it, then transition to the end. A single compositor-driven transition
      // gives a fluid, continuous fill with no per-frame steps.
      fill.style.transition = "none";
      fill.style.transform = `scaleY(${fromScale})`;
      // Force a reflow so the start value is applied before the transition.
      void fill.offsetHeight;
      fill.style.transition = `transform ${Math.max(durationMs, 0)}ms linear`;
      fill.style.transform = `scaleY(${toScale})`;
    }

    _setupIntersectionObserver() {
      if (!this.autoplay) return;

      this.intersectionObserver = new IntersectionObserver(
        (entries) => {
          const entry = entries[0];
          const visible =
            !!entry && entry.isIntersecting && entry.intersectionRatio >= 0.2;

          if (visible === this.isInViewport) return;
          this.isInViewport = visible;

          if (this.isInViewport) {
            this._resumeSlideMedia(this.visuals[this.current]);
            this._startProgress({ resume: true });
          } else {
            this._stopProgress({ freeze: true });
            this._pauseSlideMedia(this.visuals[this.current], {
              reset: false,
            });
          }
        },
        {
          threshold: [0, 0.2, 1],
        }
      );

      this.intersectionObserver.observe(this.el);
    }
  }

  function initSection(section) {
    if (
      !section ||
      !section?.classList.contains("media-content-slider-section")
    ) {
      return;
    }

    const root = section.querySelector(".media-content-slider");
    if (!root) return;

    if (root._mediaContentInstance) {
      root._mediaContentInstance.destroy();
      root._mediaContentInstance = null;
    }
    root._mediaContentInstance = new MediaContentSlider(root);

    // Fixed an issue where placeholders in other sections were not displayed because similar SVGs were in this section with visibility hidden.
    const placeholderSvgs = root.querySelectorAll(".placeholder-svg");
    placeholderSvgs.forEach((svg) => {
      svg.querySelectorAll("defs").forEach((defs) => {
        defs.remove();
      });
    });
  }

  initSection(document.currentScript.parentElement);

  if (!window.__themeMediaContentSliderEvents) {
    window.__themeMediaContentSliderEvents = true;

    document.addEventListener("shopify:section:load", function (event) {
      initSection(event.target);
    });

    document.addEventListener("shopify:section:unload", (e) => {
      e.target.querySelectorAll(".media-content-slider").forEach((el) => {
        if (!el._mediaContentInstance) return;

        el._mediaContentInstance.destroy();
        el._mediaContentInstance = null;
      });
    });
  }
})();
